import {
  Box,
  Button,
  Collapse,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Grid,
  GridItem,
} from "@chakra-ui/react";
import { Fragment, SetStateAction, useContext } from "react";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { AuthMode, FilterSet, Filters } from "@nens/ldo/api-v1";
import { LdoAuthContext } from "@nens/ldo/auth-v1";
import { Breadcrumbs, FilterTree, MapFilter } from "@nens/ldo/components";
import { useLocalStorage } from "@nens/react/hooks";
import { Extent, useThrottledCallback } from "@nens/shared-utils";

import { ScenariosList } from "./scenarios/scenarios-list";

export default function ScenarioPage() {
  const intl = useIntl();
  const { tenantId } = useContext(LdoAuthContext);
  // Filters will be stored in this component, used in all the subcomponents.
  const [authMode, actualSetAuthMode] = useLocalStorage<AuthMode>("ldo_auth_mode", "private");
  const [localFilters, localSetFilters] = useLocalStorage<Filters>("ldo_filterset", {});
  const filters = new FilterSet(localFilters);
  const setFilters = (updateOrFilterSet: SetStateAction<FilterSet>) =>
    updateOrFilterSet instanceof FilterSet
      ? localSetFilters(updateOrFilterSet.filters)
      : localSetFilters(updateOrFilterSet(filters).filters);

  // The Map.
  const [showMapFilter, actualSetShowMapFilter] = useState(false);
  const [bbox, actualSetBBox] = useState<string>("");
  const [updateWhileZooming, actualSetUpdateWhileZooming] = useState(true);

  const updateBBoxFilter = useThrottledCallback(
    (bbox: string, showMap: boolean, updateWhileZooming: boolean) => {
      if (bbox && showMap && updateWhileZooming) {
        setFilters((filters) => filters.set("bbox", bbox));
      } else {
        setFilters((filters) => filters.unset("bbox"));
      }
    },
    1000
  );

  const setAuthMode = (mode: AuthMode) => {
    // If set to public, remove status filters
    if (mode === "public") {
      const newFilters = filters.removeAll("status");
      if (newFilters !== filters) {
        setFilters(newFilters);
      }
    }
    actualSetAuthMode(mode);
  };

  const setBBox = (extent: Extent) => {
    const bbox = extent.join(",");
    actualSetBBox(bbox);
    updateBBoxFilter(bbox, showMapFilter, updateWhileZooming);
  };

  const setShowMapFilter = (mapFilter: boolean) => {
    actualSetShowMapFilter(mapFilter);
    updateBBoxFilter(bbox, mapFilter, updateWhileZooming);
  };

  const setUpdateWhileZooming = (updateWhileZooming: boolean) => {
    actualSetUpdateWhileZooming(updateWhileZooming);
    updateBBoxFilter(bbox, showMapFilter, updateWhileZooming);
  };

  // Drawer. (for mobile)
  const [drawer, setDrawer] = useState(false);

  // Checkboxes. Put them here so that they can also be shown on the map.
  const [checkboxes, setCheckboxes] = useState<Record<number, true>>({});

  const filterUI = () => (
    <>
      {showMapFilter ? (
        <Button
          variant="outline"
          w="full"
          my="16px"
          size="lg"
          onClick={() => setShowMapFilter(false)}
        >
          <FormattedMessage id="buttons.hidemapfilter" defaultMessage="Hide map" />
        </Button>
      ) : (
        <Flex
          w="full"
          h="184px"
          p="0"
          my="4"
          mx="0"
          backgroundImage={"/assets/images/bg-map.png"}
          rounded={"6px"}
          bgRepeat="no-repeat"
          justifyContent={"center"}
          alignItems={"center"}
        >
          <Button variant="solid" size="lg" onClick={() => setShowMapFilter(true)}>
            <FormattedMessage id="buttons.showonmap" defaultMessage="Show on map" />
          </Button>
        </Flex>
      )}
      <FilterTree
        authMode={authMode}
        setAuthMode={setAuthMode}
        filters={filters}
        setFilters={setFilters}
      />
    </>
  );

  return (
    <Fragment key={tenantId}>
      <Drawer isOpen={drawer} placement="left" onClose={() => setDrawer(false)}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader></DrawerHeader>
          <DrawerBody>{filterUI()}</DrawerBody>
          <DrawerFooter>&copy; 2023</DrawerFooter>
        </DrawerContent>
      </Drawer>
      <Box w="100%">
        <Breadcrumbs
          crumbs={[
            { label: intl.formatMessage({ id: "pages.home", defaultMessage: "Home" }), href: "/" },
            {
              label: intl.formatMessage({ id: "pages.scenarios", defaultMessage: "Scenarios" }),
              href: "/scenarios",
            },
          ]}
        />
      </Box>
      <Grid
        templateAreas={{
          base: `"main"`,
          md: `"nav main"`,
        }}
        gridTemplateColumns={{
          base: "1fr",
          md: "275px 1fr",
        }}
        h="200px"
        gap="1"
        color="blackAlpha.700"
        fontWeight="bold"
      >
        <GridItem
          pl="2"
          bg="white"
          area={"nav"}
          display={{
            base: "none",
            md: "grid",
          }}
        >
          <Flex flexDirection={"column"} position="sticky" top="0">
            {filterUI()}
          </Flex>
        </GridItem>
        <GridItem pl="2" bg="white" area={"main"}>
          <Collapse
            in={showMapFilter}
            animateOpacity
            style={{
              position: "relative",
              top: 0,
              zIndex: 99,
            }}
          >
            <Box color="white">
              <MapFilter
                authMode={authMode}
                filterSet={filters}
                checkboxes={checkboxes}
                setBBox={setBBox}
                updateWhileZooming={updateWhileZooming}
                setUpdateWhileZooming={setUpdateWhileZooming}
              />
            </Box>
          </Collapse>
          <ScenariosList
            authMode={authMode}
            filters={filters}
            setFilters={setFilters}
            setDrawer={setDrawer}
            top={showMapFilter ? "400px" : 0}
            checkboxes={checkboxes}
            setCheckboxes={setCheckboxes}
          />
        </GridItem>
      </Grid>
    </Fragment>
  );
}
